// Shared types and display helpers for the Vehicle Allocation screen. Mirrors
// the shape returned by /backend/fleet/getAllocationCurrent.ts (one row per
// vehicle seat) and /backend/fleet/getAllocationSeatChanges.ts (audit trail).

export type AllocationSeat = {
  run_id: string
  week_starting: string | null
  run_status: string | null
  run_at: string | null
  vehicle_id: string
  plate: string
  make: string | null
  model: string | null
  operational_state: string | null
  vehicle_status: string | null
  shift_index: number
  seat_label: string
  seat_id: string | null
  driver_id: string | null
  driver_name: string | null
  locked: boolean
  source: string | null
  is_empty_slot: boolean
  drivers_on_vehicle: number | string | null
  // A planned seat is a manager's placement the driver has already been moved
  // to, waiting on Uber to confirm it. Rendered in Fleetly Pink on the grid.
  is_planned?: boolean | null
  planned_from?: string | null
  plan_note?: string | null
  placement_basis?: string | null
  off_road?: boolean | null
  vehicle_warning?: string | null
}

// One vehicle card: the vehicle plus its seats sorted by shift_index.
export type VehicleGroup = {
  run_id: string
  vehicle_id: string
  plate: string
  make: string | null
  model: string | null
  operational_state: string | null
  seats: AllocationSeat[]
  hasLockedSeat: boolean
}

// A compact option for the "swap with" and "move to" dropdowns.
export type VehicleOption = {
  vehicle_id: string
  plate: string
  make: string | null
  model: string | null
}

export type AllocationSeatChange = {
  id: string
  change_type: string | null
  vehicle_a: string | null
  vehicle_a_plate: string | null
  vehicle_b: string | null
  vehicle_b_plate: string | null
  driver_id: string | null
  driver_name: string | null
  reason: string | null
  changed_at: string | null
}

// A seated driver who is no longer eligible to be allocated. Powers the banner.
export type IneligibleSeated = {
  seat_id: string
  registration_number: string | null
  shift_index: number
  driver_name: string | null
  block_reason: string | null
}

// An eligible driver who currently holds a seat, so they can be moved into an
// empty seat via move_allocation_seat. The manual allocation picker offers
// these under the "currently seated" group.
export type EligibleDriver = {
  driver_id: string
  driver_name: string | null
  seat_id: string
  current_vehicle_id: string
  current_plate: string | null
  current_shift_index: number
}

// An eligible driver who holds no seat in the current run, from
// v_allocation_bench. Seated into an empty seat with allocate_driver_to_seat.
export type BenchDriver = {
  driver_id: string
  driver_name: string | null
  // Set when a manager deliberately held this driver off the roster. A benched
  // driver stays on the bench list on purpose: dropping him from it is how a
  // decision becomes invisible.
  benched?: boolean
  bench_reason?: string | null
  benched_since?: string | null
  benched_by?: string | null
}

// Group seat rows into vehicle cards, seats ordered 1, 2, 3.
export function groupByVehicle(rows: AllocationSeat[]): VehicleGroup[] {
  const map = new Map<string, VehicleGroup>()
  for (const r of rows) {
    let g = map.get(r.vehicle_id)
    if (!g) {
      g = {
        run_id: r.run_id,
        vehicle_id: r.vehicle_id,
        plate: r.plate,
        make: r.make,
        model: r.model,
        operational_state: r.operational_state,
        seats: [],
        hasLockedSeat: false,
      }
      map.set(r.vehicle_id, g)
    }
    g.seats.push(r)
    if (r.locked) g.hasLockedSeat = true
  }
  for (const g of map.values()) {
    g.seats.sort((a, b) => a.shift_index - b.shift_index)
  }
  return Array.from(map.values()).sort((a, b) => a.plate.localeCompare(b.plate))
}

export function vehicleLabel(v: { plate: string; make: string | null; model: string | null }): string {
  const makeModel = [v.make, v.model].filter(Boolean).join(' ')
  return makeModel ? `${v.plate}, ${makeModel}` : v.plate
}

// Turn snake_case DB values into readable text, e.g. "fully_active" -> "Fully active".
export function humanize(value: string | null | undefined): string {
  if (!value) return 'Unknown'
  const spaced = value.replace(/_/g, ' ')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

// Full-month date, house style: "3 August 2026". Falls back to the raw value if
// it cannot be parsed.
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

export function formatFullDate(value: string | null | undefined): string {
  if (!value) return 'Unknown'
  // Accept YYYY-MM-DD directly to avoid timezone shifts on date-only values.
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
  if (m) {
    const year = Number(m[1])
    const month = Number(m[2])
    const day = Number(m[3])
    const name = MONTHS[month - 1]
    if (name) return `${day} ${name} ${year}`
  }
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return 'Unknown'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  const time = d.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit', hour12: false })
  return `${formatFullDate(value)}, ${time}`
}
